import { mapActions, mapMutations, mapState } from 'vuex'
import { ResponseTypes, LoqateCountries } from '@/enums/loqate'
import {
  FIND_ADDRESS_SUGGESTIONS,
  RETRIEVE_FULL_ADDRESS,
  VERIFY_USER_INPUTTED_ADDRESS
} from '@/utils/store/actionTypes'
import {
  SET_ADDRESS_IS_VERIFIED,
  SET_LAST_SEARCH_QUERY
} from '@/utils/store/mutationTypes'
import { mapLoqateResponseToSignupState, isValidMatch, isLoqateCountry } from '@/utils/loqate'

export default {
  data () {
    return {
      suggestions: [],
      container: '', // filter to narrow down the search
      lastSearchQuery: '',
      hasError: false,
      addressSuggestionsLoading: false,
      selectedAddressSuggestion: null,
      fillAddressManually: false
    }
  },
  computed: {
    addressSuggestionsSearchProps () {
      return {
        suggestions: this.suggestions,
        loading: this.addressSuggestionsLoading,
        initialValue: this.lastLoqateSearchQuery,
        showAddressForm: this.showAddressForm
      }
    },
    loqateAddressFormProps () {
      return {
        isLoqateCountry: this.isLoqateCountry,
        addressSuggestionsSearchProps: this.addressSuggestionsSearchProps,
        showAddressForm: this.showAddressForm
      }
    },
    showAddressForm () {
      return this.fillAddressManually || !this.isLoqateCountry || !!this.selectedAddressSuggestion || !!this.lastLoqateSearchQuery
    },
    isLoqateCountry () {
      return isLoqateCountry(this.country)
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    }),
    ...mapState('loqate', {
      loqateAddressIsVerified: state => state.addressIsVerified,
      lastLoqateSearchQuery: state => state.lastSearchQuery
    })
  },
  methods: {
    switchCountry (country) {
      this.$i18n.switchLocalePath(this.locale, country)
      this.$moment.locale(country)
    },
    setSuggestions (suggestions = []) {
      this.suggestions = suggestions
    },
    async retrieveFullAddress ({ Id }) {
      try {
        const address = await this.retrieveFullAddressAction({ Id })

        const addressCountry = address.CountryIso2.toLowerCase()

        if (this.country !== addressCountry) {
          this.switchCountry(addressCountry)
        }

        const transformedAddress = mapLoqateResponseToSignupState(address, addressCountry)

        this.selectedAddressSuggestion = transformedAddress

        return transformedAddress
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async pickAddressSuggestion ({ Type, Id, searchQuery }, callback = () => {}) {
      // If the response is not of type Address It's used as a container/filter to narrow down the search
      if (Type !== ResponseTypes.address) {
        this.container = Id
        await this.findAddressSuggestions({ Text: this.lastSearchQuery, resetContainer: false })

        return
      }

      const address = await this.retrieveFullAddress({ Id })

      this.setSuggestions([])

      if (!this.loqateAddressIsVerified) {
        this.setLoqateAddressIsVerified(true)
      }

      callback(address, searchQuery)
    },
    async findAddressSuggestions ({ Text, resetContainer = true, Countries = LoqateCountries.join(',') }) {
      this.addressSuggestionsLoading = true

      try {
        this.hasError = false

        if (resetContainer) {
          this.container = ''
        }

        const response = await this.findAddressSuggestionsAction({ Text, Container: this.container, Countries })

        const hasError = response.Items.find(suggestion => ['Cause', 'Error', 'Description', 'Resolution'].every(prop => suggestion.hasOwnProperty(prop)))

        if (hasError) {
          this.hasError = true
        } else {
          this.setSuggestions(response.Items)
          this.lastSearchQuery = Text
        }
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }

      this.addressSuggestionsLoading = false
    },
    async verifyUserInputtedAddress ({ address = {}, country = 'fr' }) {
      try {
        const response = await this.verifyUserInputtedAddressAction({ address, country })

        return {
          isValid: isValidMatch(response[0].Matches[0].AVC),
          address: response[0].Input
        }
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    ...mapActions('loqate', {
      findAddressSuggestionsAction: FIND_ADDRESS_SUGGESTIONS,
      retrieveFullAddressAction: RETRIEVE_FULL_ADDRESS,
      verifyUserInputtedAddressAction: VERIFY_USER_INPUTTED_ADDRESS
    }),
    ...mapMutations('loqate', {
      setLoqateAddressIsVerified: SET_ADDRESS_IS_VERIFIED,
      setLastLoqateSearchQuery: SET_LAST_SEARCH_QUERY
    })
  }
}
